import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../../../ui-components/table/table";
import {
  clearPickupLog,
  getPickupLog,
  pickupDetailsSelector
} from "../../../slices/pickup/pickupDetailsSlice";
import { HUMAN_FRIENDLY_ACTIONS } from "../../pickup/common";
import { PickupLogItem } from "../../../app/data/pickup/models";
import "./pickupDetails.scss";
import Tag from "ui-components/molecules/tag/tag";
import { TagColor } from "app/data/common/tagColor";
import { formatAdditionalInfo, extractRemainingTextAfterReason } from "../../pickup/utils/pickupLogUtils";

export interface PickupLogProps {
  pickupId: string;
  onRescheduled: (arg: { pickupNumber: string, pickupDate?: string, actionPerformed: string } | null) => void;
};

const columnHelper = createColumnHelper<PickupLogItem>();

const PickupDetailsLog: React.FC<PickupLogProps> = ({ onRescheduled, ...props }) => {
  const dispatch = useDispatch();
  const pickupDetailsState = useSelector(pickupDetailsSelector);

  const columns = [
    columnHelper.accessor("actionExecutedTime", {
      id: "actionExecutedTime",
      header: "Event Time",
      cell: (cellProps) => (
        <>
          {cellProps.getValue() && (
            <div className="xgs-pickup-details__log__date">
              <div>{cellProps.getValue().toUiDateFormat()}</div> <div className="xgs-pickup-details__log__gray-value">{cellProps.getValue().toUiTimeShortFormat()}</div>
            </div>
          )}
        </>
      ),
      size: 145,
    }),
    columnHelper.accessor("userExecuted", {
      id: "userExecuted",
      header: "User",
      size: 208,
    }),
    columnHelper.accessor("actionPerformed", {
      id: "actionPerformed",
      header: "Action",
      cell: (cellProps: any) => {
        const value = cellProps.getValue();
        const { driverId, driverName } = cellProps.row.original;

        if (value === 'ASSIGNED') {
          return (
            <>
              Assigned to<br />
              {driverName}{' '}
              <span className="xgs-pickup-details__log__gray-value">
                ({driverId})
              </span>
            </>
          );
        }

        return HUMAN_FRIENDLY_ACTIONS[value as keyof typeof HUMAN_FRIENDLY_ACTIONS] || value;
      },
      size: 155,
    }),
    columnHelper.display({
      id: "additionalInfo",
      size: 170,
      header: "Additional Info",
      cell: ({ row }) => {
        const data = row.original;

        const isCompletedByTerminal =
          data?.actionSource === "PORTAL" && data?.actionPerformed === "COMPLETED";

        return (
          <>
            {isCompletedByTerminal && (
              <span className="xgs-pickup-details__log__additional__item">
                <Tag mods={{ color: TagColor.RED }}>Completed at Terminal</Tag>
              </span>
            )}

            {!isCompletedByTerminal && (() => {
              const { reason, formattedText } = formatAdditionalInfo(data);

              // If no reason and no formatted text, return blank
              if (!reason && !formattedText) {
                return null;
              }

              // If no reason but has formatted text (just notes), display normally
              if (!reason && formattedText) {
                return (
                  <>
                    <span className="xgs-pickup-details__log__additional__item" style={{ fontWeight: 'normal' }}>
                      {formattedText}
                    </span>

                    {data.driverName && (
                      <span className="xgs-pickup-details__log__additional__item">
                        {data.driverName}{" "}
                        {data.driverId ? (
                          <span className="xgs-pickup-details__log__gray-value">
                            ({data.driverId})
                          </span>
                        ) : (
                          ""
                        )}
                      </span>
                    )}

                    {!data.driverName && data.driverId && (
                      <span className="xgs-pickup-details__log__additional__item">
                        {data.driverId}
                      </span>
                    )}
                  </>
                );
              }

              // If we have reason, split the formatted text to show reason in bold
              const remainingText = extractRemainingTextAfterReason(formattedText, reason);

              return (
                <>
                  <span className="xgs-pickup-details__log__additional__item">
                    <strong>{reason}</strong>
                  </span>
                  {remainingText && (
                    <>
                      {', '}
                      <span className="xgs-pickup-details__log__additional__item" style={{ fontWeight: 'normal' }}>
                        {remainingText}
                      </span>
                    </>
                  )}

                  {data.driverName && (
                    <span className="xgs-pickup-details__log__additional__item">
                      {data.driverName}{" "}
                      {data.driverId ? (
                        <span className="xgs-pickup-details__log__gray-value">
                          ({data.driverId})
                        </span>
                      ) : (
                        ""
                      )}
                    </span>
                  )}

                  {!data.driverName && data.driverId && (
                    <span className="xgs-pickup-details__log__additional__item">
                      {data.driverId}
                    </span>
                  )}
                </>
              );
            })()}
          </>
        );
      },
    })
  ];

  useEffect(() => {
    if (!props.pickupId) return;
    dispatch(getPickupLog(props.pickupId));
  }, [props.pickupId, dispatch]);

  useEffect(() => {
    if (!pickupDetailsState.pickupLog) return;
    let rescheduledToEntry = pickupDetailsState.pickupLog.find(entry => entry.actionPerformed === "RESCHEDULED_TO");
    let rescheduledEntry = pickupDetailsState.pickupLog.find(entry => entry.actionPerformed === "RESCHEDULE_PICKUP");
    const entry = rescheduledEntry || rescheduledToEntry || null;
    onRescheduled(entry ? { pickupNumber: entry.pickupNumber, pickupDate: entry.pickupDate, actionPerformed: entry.actionPerformed } : null);
  }, [pickupDetailsState.pickupLog, onRescheduled]);

  useEffect(() => {
    return () => { dispatch(clearPickupLog()) };
  }, [dispatch]);

  return (
    <div className="xgs-pickup-details__log">
      <Table
        isLoading={pickupDetailsState.requestStarted && (pickupDetailsState.requestCreator === "GET_PICKUP_LOG")}
        columns={columns}
        data={pickupDetailsState.pickupLog}
        minTableHeight={220}
        noResultsText="No entries"
      />
    </div>
  );
};

export default PickupDetailsLog;
